import { useState } from 'react';
import { AppLoader } from '@/components/AppLoader';
import { FadeInOut } from '@/components/FadeInOut';
import { QuickCreateErrorMessage } from '@/components/QuickCreateErrorMessage';
import { useCurrentUserContext } from '@/contexts';
import { useHandleConnectFailed } from '@/features/canva/hooks/useHandleConnectFailed';
import { ImagePicker } from '@/features/common/components/ImagePicker';
import { useCollection } from '@/features/events/contexts/CollectionContext/useCollection';
import { Bootstrap } from '@/features/magic/components/Bootstrap';
import { isNotFoundError } from '@/features/magic/utils/isNotFoundError';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useMetaThemeColor } from '@/hooks/useMetaThemeColor';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Header } from '@/router/components/MagicTabsLayout/components/Header';
import { MagicJobType } from '@/types/gql.generated';
import { Fade } from '@/ui';
import type { QueryError } from '@/utils/errors';
import { AuthModal } from '../../components/AuthModal';
import { BadgeGroup, PlatformBadge } from '../../components/Badges';
import canva from '../../components/Badges/assets/canva.png';
import { CenteredContent } from '../../components/CenteredContent';
import { Layout } from '../../components/Layout';
import { LoadingCard } from '../../components/LoadingCard';
import { MaintenanceMode } from '../../components/MaintenanceMode';
import { PreviewMagicExperience } from '../../components/PreviewMagicExperience';
import { ImagePromptForm } from '../../components/PromptForm/ImagePromptForm';
import { useJobContext } from '../../contexts/JobContext/useJobContext';
import { useMagic, useMagicPrompt } from '../../contexts/MagicContext';
import { useBootstrappedItem } from '../../hooks/useBootstrappedItem';
import { useCreateExperienceFromMagicJob } from '../../hooks/useCreateExperienceFromMagicJob';
import { useJobComplete } from '../../hooks/useJobComplete';
import type { AppState } from '../../types';
import { NotFound } from '../NotFound';
import { Dropzone } from './Dropzone';
import { Hero } from './Hero';

type GetContentKeyArg = {
  bootstrappedItemError: QueryError | null;
  generalError: QueryError | null;
  state: AppState;
};

const getContentKey = ({
  bootstrappedItemError,
  generalError,
  state,
}: GetContentKeyArg): string => {
  if (isNotFoundError(bootstrappedItemError)) {
    return 'notfound';
  }
  if (generalError) {
    return 'error';
  }
  if (state === 'loading') {
    return 'loading';
  }
  return 'prompt';
};

const MagicCanvaContent = () => {
  const { isAuthenticated } = useCurrentUserContext();
  const { trackEvent } = useAnalytics();
  const { state, resetAppState, promptSource, promptType } = useMagic();
  const { prompt, instructions } = useMagicPrompt();
  const { reset: resetCreateJob, createJob } = useJobContext();
  const { base } = useCollection();
  const job = base?.job;
  const { bootstrappedItemLoading, error: bootstrappedItemError = null } =
    useBootstrappedItem();
  const {
    createExperience,
    isPending: isCreatingExperience,
    error: createError,
    reset: resetCreate,
  } = useCreateExperienceFromMagicJob();
  const [isPreview, setIsPreview] = useState(false);
  const error: QueryError | null = createError || bootstrappedItemError;

  usePageTitle('Turn Canva images into a page everyone will love');
  useMetaThemeColor('#ECE3FB');

  useHandleConnectFailed();

  useJobComplete((job) => {
    if (isAuthenticated) {
      createExperience(job);
    } else {
      setIsPreview(true);
    }
  });

  const handleCreateSubmit = () => {
    trackEvent('quick-create:click generate');
    resetCreateJob();
    createJob({
      instructions,
      prompt,
      promptSource,
      promptType,
    });
  };

  const handleReset = () => {
    resetAppState();
    resetCreate();
    setIsPreview(false);
  };

  return (
    <FadeInOut key={String(isPreview)} w="100%">
      {bootstrappedItemLoading || isCreatingExperience ? (
        <AppLoader
          bgImage="linear-gradient(165.09deg, #ECE3FB 7.13%, #F5F1FA 50.03%)"
          h="100vh"
          layerStyle="magicPageBackground"
        />
      ) : isPreview && job ? (
        <Fade in>
          <PreviewMagicExperience job={job} />
        </Fade>
      ) : (
        <Layout
          align="center"
          bgImage="linear-gradient(165.09deg, #ECE3FB 7.13%, #F5F1FA 50.03%)"
          layerStyle="magicPageBackground"
          minH="100vh"
          badges={
            <BadgeGroup>
              <PlatformBadge
                alt="Canva App Store"
                href="https://www.canva.com/apps/"
                maxH={{ base: '48px', md: '60px' }}
                src={canva}
              />
            </BadgeGroup>
          }
        >
          <Header />

          <Hero mb="6" w="100%" />

          <FadeInOut
            w="100%"
            key={getContentKey({
              bootstrappedItemError,
              generalError: error,
              state,
            })}
          >
            <CenteredContent gap="6">
              {isNotFoundError(bootstrappedItemError) ? (
                <NotFound />
              ) : error ? (
                <QuickCreateErrorMessage error={error} onReset={handleReset} />
              ) : state === 'loading' ? (
                <LoadingCard />
              ) : (
                <ImagePromptForm
                  cta={(props) => <Dropzone {...props} />}
                  onSubmit={handleCreateSubmit}
                />
              )}
            </CenteredContent>
          </FadeInOut>
        </Layout>
      )}
    </FadeInOut>
  );
};

export const CanvaPage = () => {
  return (
    <MaintenanceMode>
      <Bootstrap promptType={MagicJobType.File}>
        <MagicCanvaContent />

        <AuthModal />
        <ImagePicker />
      </Bootstrap>
    </MaintenanceMode>
  );
};
